import { MdOutlineFileOpen, MdOutlineInsertDriveFile } from 'react-icons/md';
import { HiddenFileInput } from 'views/Dialer/list/components/styled';
import React, { useState, memo } from 'react';
import Dropzone from 'react-dropzone';
import { FileInputRoot } from './styled';
import { toast } from 'react-toastify';

/**
 * Elemento genérico para utilização de Drag'n'drop
 * @param {import('react-dropzone').DropzoneProps} props
 * @param {string} props.acceptedFileExtensions - String contendo as extensões aceitas, separadas por vírgula. Ex: '.csv, .pdf, .txt'.
 * @param {string} props.name - O `name` do input.
 * @param {string} props.id - O `id` do input.
 * @param {(validatedFiles: File[], invalidatedFiles: FileRejection[], _event) => void} props.changeFile - O callback que será executado quando um arquivo for enviado.
 * @param {string} props.description - O texto a ser exibido no input antes de um arquivo ser enviado. Por default é 'Clique ou arraste para adicionar arquivos'.
 * @param {boolean} props.isDisabled - Se a dropzone deve estar desabilitada.
 */
export const DragAndDrop = memo(
  ({
    acceptedFileExtensions,
    multiple = false,
    id,
    name,
    changeFile,
    description,
    isDisabled = false
  }) => {
    const [isBeingHovered, setIsBeingHovered] = useState(false);

    return (
      <Dropzone
        disabled={isDisabled}
        onDrop={(acceptedFiles, rejectedFiles, _event) => {
          setIsBeingHovered(false);

          // Quando arquivos são dropados na dropzone, o atributo `accept` do input é ignorado,
          // fazendo com que o react-dropzone classifique qualquer arquivo como aceito, mesmo quando o formato não compreende ao especificado.
          // Por isso, é necessário verificar manualmente se os arquivos dropados são válidos antes de passás-lo adiante.
          let validatedFiles = [];
          let invalidatedFiles = rejectedFiles;

          // Quando enviar multiplos arquivos em um dropdown que so aceita um, o primeiro que foi rejeitado deve ser retornado aos arquivos aceitos
          if (!multiple) {
            invalidatedFiles = rejectedFiles.filter((file) => {
              if (acceptedFiles.length) return true;

              if (file.errors.some(({ code }) => code == 'too-many-files')) {
                acceptedFiles.push(file.file);
                return false;
              }

              return true;
            });

            acceptedFiles.length = 1;
          }

          acceptedFiles.forEach((file) => {
            const fileExtension = `.${file.name.split('.').at(-1)}`;

            acceptedFileExtensions.includes(fileExtension) ||
            acceptedFileExtensions.includes(file.type)
              ? validatedFiles.push(file)
              : invalidatedFiles.push({
                  file,
                  errors: [
                    { code: 'file-invalid-type', message: 'Invalid file type' }
                  ]
                });
          });

          if (!validatedFiles.length)
            return toast.error(
              'Nenhum dos arquivos que você enviou foi aceito.',
              { autoClose: 3000, closeOnClick: true }
            );
          if (invalidatedFiles.length)
            toast.error(
              `Os seguintes arquivos não foram aceitos: ${invalidatedFiles
                .map(({ file }) => file.name)
                .join(', ')}.`,
              { autoClose: 3000, closeOnClick: true }
            );

          changeFile(validatedFiles, invalidatedFiles, _event);
        }}
        multiple={multiple}
        onDragEnter={() => setIsBeingHovered(true)}
        onDragLeave={() => setIsBeingHovered(false)}
      >
        {({ getRootProps, getInputProps }) => (
          <FileInputRoot
            isBeingHovered={isBeingHovered}
            isDisabled={isDisabled}
            {...getRootProps({
              display: 'contents'
            })}
          >
            {isBeingHovered ? (
              <>
                <MdOutlineFileOpen size={32} />
                Solte para enviar
              </>
            ) : (
              <>
                <MdOutlineInsertDriveFile size={32} />
                {description ?? 'Clique ou arraste para adicionar arquivos'}
              </>
            )}
            <HiddenFileInput
              {...getInputProps({
                id,
                name,
                accept: acceptedFileExtensions
              })}
              type="file"
            />
          </FileInputRoot>
        )}
      </Dropzone>
    );
  },
  (prev, next) => JSON.stringify(prev) === JSON.stringify(next)
);

DragAndDrop.displayName = 'DragAndDrop';
